
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth';

interface CandidateProfile {
  user_id: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  // Add other profile fields as necessary
}

interface UseCandidateProfileResult {
  profile: CandidateProfile | null | undefined;
  loading: boolean;
  error: string | null;
  loadProfile: () => Promise<void>;
  updateProfile: (updates: Partial<CandidateProfile>) => Promise<void>;
  // Add these properties that components expect
  data: CandidateProfile | null | undefined;
  isLoading: boolean;
}

export const useCandidateProfile = (userId?: string): UseCandidateProfileResult => {
  const { user } = useUser();
  const [profile, setProfile] = useState<CandidateProfile | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    if (!userId) {
      console.warn('No user ID provided to load profile.');
      setProfile(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId as any)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        setError(error.message);
        setProfile(null);
      } else {
        setProfile(data as any as CandidateProfile);
      }
    } catch (err: any) {
      console.error('Unexpected error loading profile:', err);
      setError(err.message);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateProfile = useCallback(async (updates: Partial<CandidateProfile>) => {
    if (!userId) {
      console.warn('No user ID provided to update profile.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(updates as any)
        .eq('user_id', userId as any);

      if (error) {
        console.error('Error updating profile:', error);
        setError(error.message);
      } else {
        setProfile((prevProfile: any) => ({ ...prevProfile, ...updates }));
      }
    } catch (err: any) {
      console.error('Unexpected error updating profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (user && userId) {
      loadProfile();
    } else {
      setProfile(null);
    }
  }, [user, userId]); // loadProfile is stable due to useCallback with [userId]

  return { 
    profile, 
    loading, 
    error, 
    loadProfile, 
    updateProfile,
    // Alias properties that components expect
    data: profile,
    isLoading: loading
  };
};
